import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SafeDialog } from "components/Dialog";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document";
import { DocumentType } from "generated-graphql/graphql";
import { useSwapSubmissionDocument } from "hooks/useSubmissionMutations";
import { useCallback, useMemo, useState } from "react";

type UploadedDocument = {
  id: string;
  title: string;
  documentType?: DocumentType;
};

interface SwapSubmissionDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  submissionDocumentId: string;
  tenantId: string;
  currentDocumentName: string;
  currentDocumentType?: DocumentType;
  onSuccess?: () => Promise<void> | void;
}

export function SwapSubmissionDocumentDialog({
  open,
  onClose,
  submissionId,
  submissionDocumentId,
  tenantId,
  currentDocumentName,
  currentDocumentType,
  onSuccess,
}: SwapSubmissionDocumentDialogProps) {
  const alerts = useAlerts();
  const [verified, setVerified] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [uploadDialogState, setUploadDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [swapSubmissionDocument, { loading }] = useSwapSubmissionDocument();
  const defaultDocumentType = useMemo(
    () => currentDocumentType ?? DocumentType.Other,
    [currentDocumentType]
  );

  const resetAndClose = useCallback(() => {
    setVerified(false);
    setUploadedDocument(null);
    setUploadDialogState(defaultAddEditDocumentDialogState);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }
    resetAndClose();
  }, [loading, resetAndClose]);

  const handleOpenUploadDialog = useCallback(() => {
    setUploadDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.ADD,
      step: FormState.UPLOAD,
      documentType: defaultDocumentType,
    });
  }, [defaultDocumentType]);

  const handleSwap = useCallback(async () => {
    if (!uploadedDocument?.id) {
      alerts.error("Please upload a replacement document before saving");
      return;
    }

    try {
      await swapSubmissionDocument({
        variables: {
          input: {
            submissionId,
            submissionDocumentId,
            documentId: uploadedDocument.id,
          },
        },
      });

      alerts.success("Submission document swapped successfully");
      await onSuccess?.();
      resetAndClose();
    } catch (error) {
      alerts.error("Failed to swap submission document", error as Error);
    }
  }, [
    alerts,
    onSuccess,
    resetAndClose,
    submissionDocumentId,
    submissionId,
    swapSubmissionDocument,
    uploadedDocument?.id,
  ]);

  const canSave = Boolean(uploadedDocument?.id && verified && !loading);

  return (
    <>
      <SafeDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Swap Submission Document</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="warning">
              You are about to swap out the current submission document with a new one. This will
              not reset reviews.
            </Alert>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Current document: {currentDocumentName}
              </Typography>
            </Box>

            <Box>
              <Button variant="outlined" onClick={handleOpenUploadDialog} disabled={loading}>
                Upload replacement document
              </Button>
            </Box>

            {uploadedDocument ? (
              <Typography variant="body2" color="text.secondary">
                Replacement document: {uploadedDocument.title}
              </Typography>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={verified}
                  onChange={(event) => setVerified(event.target.checked)}
                  disabled={loading}
                />
              }
              label="I verify that this submission document needs to be swapped out."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSwap}
            disabled={!canSave}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </SafeDialog>

      {uploadDialogState.open && (
        <AddEditDocumentDialog
          dialogState={uploadDialogState}
          setDialogState={setUploadDialogState}
          tenantId={tenantId}
          showFacilityPicker={false}
          refetchQueries={[]}
          onSubmit={(document) => {
            setUploadedDocument({
              id: document.id,
              title: document.title,
              documentType: document.documentType,
            });
          }}
        />
      )}
    </>
  );
}
